import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
  DialogBase,
  DialogService,
  HotkeyService,
  tr,
  sort,
  currentLanguage,
  languageMap,
} from '@kpd/framework-ngx';
import { Permissionset } from 'app/shared/models/permissionset';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { map, tap, catchError } from 'rxjs/operators';
import { Permission } from 'app/shared/models/permission';
import { of, combineLatest } from 'rxjs';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-permission-set-wizard-import',
  templateUrl: './permissionset-details-wizard-import.component.html',
})
export class PermissionsetWizardImportComponent extends DialogBase {
  @Input() permissionSet: Permissionset;
  activeStep = 0;
  types = [
    { label: tr('Replace current set'), value: 'replace' },
    { label: tr('Add to current set'), value: 'add' },
  ];
  selectedType = 'replace';
  permissionSets: Permissionset[] = [];
  permissionSetLoading = false;
  permissionSetsColumns = [
    { field: 'name', header: tr('Name') },
    { field: 'description', header: tr('Description') },
  ];
  selectedPermissionSet: Permissionset | null;
  verifyColumns = [{ field: 'name', header: tr('Name') }];
  changedPermissions: { add: Permission[]; delete: Permission[] } = { add: [], delete: [] };
  addPermissionSelection = {};
  deletePermissionSelection = {};
  importLoading = false;
  feedback: { success: boolean; message?: string; name: string; type: string }[] = [];
  currentLanguage = currentLanguage;

  @ViewChild('feedbackScroll', { static: false })
  feedbackScroll: any;

  constructor(
    private permissionsetService: PermissionsetService,
    private localizationService: UMLocalizationService,
    dialogService: DialogService,
    element: ElementRef,
    hotKeyService: HotkeyService,
  ) {
    super('importPermissions', dialogService, element, hotKeyService);
  }

  onBeforeShow(): void {
    this.feedback = [];
    this.activeStep = 0;
    this.selectedPermissionSet = null;
    this.changedPermissions = { add: [], delete: [] };
    this.addPermissionSelection = {};
    this.deletePermissionSelection = {};
  }

  onConfirmType(): void {
    if (!this.permissionSet.application) {
      return;
    }
    this.permissionSetLoading = true;
    this.permissionsetService
      .getUserPermissionSets(this.permissionSet.application)
      .pipe(map((result) => result.filter((set) => set.type === 'template')))
      .subscribe(
        (result) => {
          this.permissionSetLoading = false;
          this.permissionSets = result.map((set) => {
            set.key.isGlobalPermission = true;
            set.description = set.description[this.getLanguage()];
            return set;
          });
        },
        () => (this.permissionSetLoading = false),
      );
    this.activeStep = 1;
  }

  private getLanguage(): string {
    const langCode = this.localizationService.getLanguage();
    if (langCode) {
      return languageMap[langCode];
    }
    return 'english';
  }

  onConfirmPermissionSet(): void {
    if (!this.selectedPermissionSet) {
      return;
    }
    this.permissionSetLoading = true;
    this.permissionsetService.getById(this.selectedPermissionSet.key).subscribe((result) => {
      this.changedPermissions = this.comparePermissions(
        this.permissionSet.permissions as Permission[],
        result.permissions as Permission[],
        this.selectedType,
      );
      this.changedPermissions.add.forEach((permission) => {
        this.addPermissionSelection[permission.key.toString()] = permission;
      });
      this.changedPermissions.delete.forEach((permission) => {
        this.deletePermissionSelection[permission.key.toString()] = permission;
      });
      this.permissionSetLoading = false;
      this.activeStep = 2;
    });
  }

  private comparePermissions(
    oldPermissions: Permission[],
    newPermissions: Permission[],
    type: string,
  ): { add: Permission[]; delete: Permission[] } {
    const addedPermissions = newPermissions.filter((permission) => {
      return !oldPermissions.some((p) => p.id === permission.id);
    });
    let removedPermissions: Permission[] = [];
    if (type === 'replace') {
      removedPermissions = oldPermissions.filter((permission) => {
        return !newPermissions.some((p) => p.id === permission.id);
      });
    }
    return { add: addedPermissions, delete: removedPermissions };
  }

  onCheckboxChange(event: any, permission: Permission, add: boolean): void {
    if (
      !event.target.checked && add
        ? this.addPermissionSelection[permission.key.toString()]
        : this.deletePermissionSelection[permission.key.toString()]
    ) {
      if (add) {
        delete this.addPermissionSelection[permission.key.toString()];
        return;
      }
      delete this.deletePermissionSelection[permission.key.toString()];
      return;
    }
    if (add) {
      this.addPermissionSelection[permission.key.toString()] = permission;
      return;
    }
    this.deletePermissionSelection[permission.key.toString()] = permission;
  }

  onCheckboxChangeAll(event: any, add: boolean): void {
    if (event.target.checked) {
      if (add) {
        this.changedPermissions.add.forEach((item) => (this.addPermissionSelection[item.key.toString()] = item));
        return;
      }
      this.changedPermissions.delete.forEach((item) => (this.deletePermissionSelection[item.key.toString()] = item));
      return;
    }
    if (add) {
      this.addPermissionSelection = {};
      return;
    }
    this.deletePermissionSelection = {};
  }

  get deletePermissionSelectionCount(): number {
    return Object.keys(this.deletePermissionSelection).length;
  }

  get addPermissionSelectionCount(): number {
    return Object.keys(this.addPermissionSelection).length;
  }

  importPermissions(): void {
    this.activeStep = 3;
    this.importLoading = true;
    let observers = Object.keys(this.addPermissionSelection).map((key) =>
      this.permissionsetService
        .addPermissionToPermissionSet(this.permissionSet.key, this.addPermissionSelection[key].key)
        .pipe(
          tap(() => {
            this.feedback.push({
              success: true,
              type: 'add',
              name: this.addPermissionSelection[key].name,
            });
          }),
          catchError(() => {
            this.feedback.push({
              success: false,
              type: 'add',
              message: 'test',
              name: this.addPermissionSelection[key].name,
            });
            setTimeout(() => {
              this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
            });
            return of();
          }),
          tap(() => {
            setTimeout(() => {
              this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
            });
          }),
        ),
    );
    observers = [
      ...observers,
      ...Object.keys(this.deletePermissionSelection).map((key) =>
        this.permissionsetService
          .removePermissionFromPermissionSet(this.permissionSet.key, this.deletePermissionSelection[key].key)
          .pipe(
            tap(() => {
              this.feedback.push({
                success: true,
                type: 'delete',
                name: this.deletePermissionSelection[key].name,
              });
            }),
            catchError(() => {
              this.feedback.push({
                success: false,
                type: 'delete',
                message: 'test',
                name: this.deletePermissionSelection[key].name,
              });
              setTimeout(() => {
                this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
              });

              return of();
            }),
            tap(() => {
              setTimeout(() => {
                this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
              });
            }),
          ),
      ),
    ];

    combineLatest(...observers).subscribe(
      () => {},
      () => {
        this.importLoading = false;
      },
      () => {
        this.importLoading = false;
      },
    );
  }

  get sortedFeedback(): any {
    return sort(this.feedback, [{ name: 'success' }, { name: 'type' }]);
  }

  get allSucceed(): boolean {
    return !this.feedback.some((item) => !item.success);
  }

  get importErrors(): any {
    return this.feedback.filter((item) => !item.success);
  }
}
